/**
 * G12 / V32: blocked→known latency. A "blocked" moment is a run needing the user (ask, approval,
 * landing failure, device alert). Each moment collects timestamps as it travels:
 *
 *   blockedAt  agent event time (daemon clock)         hubAt     hub recorded the inbox item
 *   sentAt     hub sent the Web Push                   receivedAt service worker got the push
 *   shownAt    notification shown / item first seen    openedAt  user opened it
 *   resolvedAt the ask left the inbox (answered/approved/landed)
 *
 * Only these numbers are recorded (no content), sealed as the account's `latency` setting entity
 * so they sync across the user's devices and the hub never sees them. Clocks differ between the
 * daemon, hub and browser, so a stage that comes out negative is dropped, not clamped.
 */
export type LatencyKind = 'question' | 'approval' | 'landing' | 'alert' | 'other'

export interface Stamps {
  blockedAt?: number
  hubAt?: number
  sentAt?: number
  receivedAt?: number
  shownAt?: number
  openedAt?: number
  resolvedAt?: number
}
export type StampKey = keyof Stamps

export interface LatencySample {
  id: string
  kind: LatencyKind
  stamps: Stamps
  /** When the sample was finalized (window filtering and ordering). */
  at: number
}

export interface StageDef {
  key: string
  label: string
  from: StampKey
  to: StampKey
  hint: string
}

/** Blocked→known is the sum of the first four; the rest is the user's own time. */
export const STAGES: readonly StageDef[] = [
  {
    key: 'report',
    label: 'Agent to hub',
    from: 'blockedAt',
    to: 'hubAt',
    hint: 'Daemon report until the hub recorded the item.',
  },
  {
    key: 'hold',
    label: 'Hub queue',
    from: 'hubAt',
    to: 'sentAt',
    hint: 'Coalescing, quiet hours and caps before the push left.',
  },
  {
    key: 'push',
    label: 'Push delivery',
    from: 'sentAt',
    to: 'receivedAt',
    hint: 'Push service to this device.',
  },
  {
    key: 'show',
    label: 'Notification shown',
    from: 'receivedAt',
    to: 'shownAt',
    hint: 'Decrypting and showing it.',
  },
  {
    key: 'open',
    label: 'Until opened',
    from: 'shownAt',
    to: 'openedAt',
    hint: 'Your time to react.',
  },
  {
    key: 'resolve',
    label: 'Until resolved',
    from: 'openedAt',
    to: 'resolvedAt',
    hint: 'Your time to answer.',
  },
]

export const KNOWN: StageDef = {
  key: 'known',
  label: 'Blocked to known',
  from: 'blockedAt',
  to: 'shownAt',
  hint: 'From the run becoming blocked to a notification (or the Inbox) showing it.',
}
export const RESOLVED: StageDef = {
  key: 'resolved',
  label: 'Blocked to resolved',
  from: 'blockedAt',
  to: 'resolvedAt',
  hint: 'The whole loop, including your own time.',
}

export function duration(s: Stamps, d: Pick<StageDef, 'from' | 'to'>): number | undefined {
  const a = s[d.from]
  const b = s[d.to]
  if (a === undefined || b === undefined) return undefined
  const ms = b - a
  return ms >= 0 ? ms : undefined
}

/** Nearest-rank percentile (p in 0..100) of unsorted values; undefined when empty. */
export function percentile(values: readonly number[], p: number): number | undefined {
  if (!values.length) return undefined
  const sorted = [...values].sort((x, y) => x - y)
  const rank = Math.max(1, Math.ceil((p / 100) * sorted.length))
  return sorted[Math.min(sorted.length, rank) - 1]
}

export interface StageStat {
  key: string
  label: string
  hint: string
  n: number
  p50?: number
  p95?: number
  /** The slowest samples, worst first (drill-down; ids only). */
  slowest: { id: string; kind: LatencyKind; ms: number }[]
}

export interface LatencySummary {
  count: number
  known: StageStat
  resolved: StageStat
  stages: StageStat[]
}

export const WINDOW_MS = 14 * 86_400_000
export const MAX_SAMPLES = 300

function stat(def: StageDef, samples: readonly LatencySample[]): StageStat {
  const rows = samples.flatMap((s) => {
    const ms = duration(s.stamps, def)
    return ms === undefined ? [] : [{ id: s.id, kind: s.kind, ms }]
  })
  const values = rows.map((r) => r.ms)
  const p50 = percentile(values, 50)
  const p95 = percentile(values, 95)
  return {
    key: def.key,
    label: def.label,
    hint: def.hint,
    n: rows.length,
    ...(p50 !== undefined ? { p50 } : {}),
    ...(p95 !== undefined ? { p95 } : {}),
    slowest: rows.sort((a, b) => b.ms - a.ms).slice(0, 5),
  }
}

export function summarize(
  samples: readonly LatencySample[],
  now: number,
  filter: { kind?: LatencyKind; windowMs?: number } = {},
): LatencySummary {
  const since = now - (filter.windowMs ?? WINDOW_MS)
  const inWindow = samples.filter((s) => s.at >= since && (!filter.kind || s.kind === filter.kind))
  return {
    count: inWindow.length,
    known: stat(KNOWN, inWindow),
    resolved: stat(RESOLVED, inWindow),
    stages: STAGES.map((d) => stat(d, inWindow)),
  }
}

/** Compact duration: "850 ms", "4.2 s", "3 min 05 s". Never truncates a number. */
export function formatMs(ms: number | undefined): string {
  if (ms === undefined) return 'no data'
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 10_000) return `${(ms / 1000).toFixed(1)} s`
  if (ms < 60_000) return `${Math.round(ms / 1000)} s`
  const m = Math.floor(ms / 60_000)
  const s = Math.round((ms % 60_000) / 1000)
  return `${m} min ${String(s).padStart(2, '0')} s`
}

/** Union by id (newer stamps win per key), newest first, capped. */
export function mergeSamples(
  a: readonly LatencySample[],
  b: readonly LatencySample[],
  cap = MAX_SAMPLES,
): LatencySample[] {
  const by = new Map<string, LatencySample>()
  for (const s of [...a, ...b]) {
    const was = by.get(s.id)
    by.set(
      s.id,
      was ? { ...(s.at >= was.at ? s : was), stamps: { ...was.stamps, ...s.stamps } } : s,
    )
  }
  return [...by.values()].sort((x, y) => y.at - x.at || x.id.localeCompare(y.id)).slice(0, cap)
}

export function kindOf(item: {
  type?: string
  category?: string
  derived?: boolean
  id: string
}): LatencyKind {
  if (item.type === 'question') return 'question'
  if (item.type === 'approval') return 'approval'
  if (item.category === 'device-alert' || item.id.startsWith('a:')) return 'alert'
  if (item.type === 'land' || item.category === 'landing-failed' || item.id.startsWith('l:'))
    return 'landing'
  return 'other'
}

export interface TrackedItem {
  id: string
  type?: string
  category?: string
  derived?: boolean
  blocking: boolean
  createdAt: number
  event?: { ts: number }
}

/**
 * Watches the Inbox. An item that was blocking and has vanished was resolved; its stamps (page
 * side: hubAt/shownAt/resolvedAt, plus whatever the service worker noted) become a sample.
 */
export class LatencyTracker {
  private seen = new Map<string, { kind: LatencyKind; stamps: Stamps }>()

  /** Returns samples for items that left the Inbox since the last observation. */
  observe(
    items: readonly TrackedItem[],
    now: number,
    opts: { visible: boolean; pending?: (id: string) => Stamps | undefined },
  ): LatencySample[] {
    const live = new Set<string>()
    for (const i of items) {
      if (!i.blocking) continue
      live.add(i.id)
      let t = this.seen.get(i.id)
      if (!t) {
        t = {
          kind: kindOf(i),
          stamps: {
            hubAt: i.createdAt,
            blockedAt: i.event?.ts ?? i.createdAt,
          },
        }
        this.seen.set(i.id, t)
      }
      // First moment the user could actually see it in the app (no notification needed).
      if (opts.visible && t.stamps.shownAt === undefined) t.stamps.shownAt = now
    }
    const out: LatencySample[] = []
    for (const [id, t] of [...this.seen]) {
      if (live.has(id)) continue
      this.seen.delete(id)
      const sw = opts.pending?.(id) ?? {}
      const stamps: Stamps = { ...t.stamps, ...sw, resolvedAt: now }
      // The earliest of "notification shown" and "seen in the app" is when the user knew.
      const shown = [t.stamps.shownAt, sw.shownAt].filter((x): x is number => x !== undefined)
      if (shown.length) stamps.shownAt = Math.min(...shown)
      out.push({ id, kind: t.kind, stamps, at: now })
    }
    return out
  }
}
